/**
 * ProjectsView.jsx - the Projects tab: won work for the selected year in a
 * filterable, sortable table, with a pinned totals bar and a detail panel.
 *
 * "Project" means won work only (src/data/projectStatus.js) - the open/lost
 * bid pipeline lives on the Opportunities tab, which is what keeps the two
 * tabs from showing the same rows.
 *
 * GlobalOps is the record for projects (10/1/26): the quote list was imported
 * one last time and nothing here points at a sheet any more. "Net terms"
 * (projects.terms, the payment terms) sits right after PO # and is filtered
 * and exported like every other column.
 *
 * r49: an Engineer column (projects.engineer - one name, or several joined
 * with commas by EngineerMultiSelect) sits with Appr. Mgr. Its filter lists
 * each engineer once, so ticking one person finds every job they are on.
 *
 * Opening a project shows it in the record editor as a full page
 * (components/opportunities/EditOpportunityModal, `asPage`): the same editor
 * an opportunity opens in - one record, one way to edit it (Sharif, 10/8/26:
 * "for Projects, Edit pops up a box"). Its Summary is editable at once, then
 * Save; the read-only project page and its Edit pop-up are retired. "+ New
 * Project" opens that editor too, as a project (status Won).
 *
 * Each person can make the table their own (Sharif, 10/9/26): show, hide and
 * reorder the columns (components/projects/projectColumns.js is the list),
 * highlight rows or cells by rule, and save it all with the filters, sort and
 * search as named views that follow them to any device
 * (components/tableViews/). The Won totals follow the columns, and Export CSV
 * writes exactly what is on screen. GLOBAL KSA keeps its own views.
 * Round 2 (10/9/26): drag a header to move it or its right edge to resize it
 * (widths are saved with the view), and Export Excel writes the screen as an
 * .xlsx with the highlight colours, widths, real numbers and dates.
 *
 * Main project > sub-project > PO line (Sharif, 10/14/26 - "make it look like
 * Sage"). The links are set by hand only (a record's Main project,
 * projects.parent_id - data/projectHierarchy); with none set the table is
 * exactly as before. Once some are, "Group by main project" (saved with the
 * view, on by default) puts records under their main project. Group rows
 * carry the sums of the records under them and open the main record on its
 * Sub-projects tab; the totals row, the counts and the export are still the
 * records, so nothing is counted twice - the export adds a Main project
 * column. Right-click a row for "Group under..." / "Remove from group" and
 * "Add PO line" / "Add sub-project". GLOBAL KSA stays flat.
 *
 * Neighbours: components/projects/* (table pieces), views/projects/* (this
 * view's filters, numbers, export and row cells), the record editor in
 * components/opportunities/, utils/projectMoney.
 */
import { useState, useEffect, useMemo } from 'react';
import { Icon } from '../components/ui/Icon';
import { Toast } from '../components/ui/Toast';
import { Button, Chip, SearchInput } from '../components/ui';
import { renderFilterPopover } from '../components/ui/BidTablePrimitives';
import { useApp } from '../context/AppContext';
import { canEdit } from '../data/roles';
import { useMyAccess } from '../hooks/useMyAccess';
import { useSyncedStatusChips } from '../hooks/useSyncedStatusChips';
import { fmt } from '../utils/format';
import { isProjectRow } from '../data/projectStatus';
import { EditOpportunityModal } from '../components/opportunities/EditOpportunityModal';
import WonTotalsRow from '../components/projects/WonTotalsRow';
import { PROJECT_COLUMNS } from '../components/projects/projectColumns';
import { tableLayout } from '../components/tableViews/viewConfig';
import { useTableView } from '../components/tableViews/useTableView';
import TableViewsBar from '../components/tableViews/TableViewsBar';
import { usePinnedHeight } from '../components/tableViews/TotalsRow';
import { rowHighlights, highlightStyle, cellLook, hoverProps } from '../components/tableViews/highlights';
import { usePointerSort } from '../components/tableViews/usePointerSort';
import { lookOf, alignOf, usePinnedLefts, pinStyle } from '../components/tableViews/tableLook';
import { useDocIndex } from '../hooks/useDocIndex';
import { useCommentsIndex } from '../hooks/useCommentsIndex';
import QuickAddMenu from '../components/tasks/QuickAddMenu';
import { useProjectTasksIndex } from '../components/tasks/useTasks';
import { HEALTH, healthOf, rowTint, rowHover } from '../data/projectHealth';
import { parseHashFilters } from '../utils/hashParams';
import { useDataScope } from '../context/DataScope';
import { parentIdOf, recordLabel, descendantIds, rollupRow } from '../data/projectHierarchy';
import { useProjectTree } from '../components/projects/hierarchy/useProjectTree';
import { TreeControls, TreeGroupLabel, TreeIndent, groupLook, matchStyle } from '../components/projects/hierarchy/TreeParts';
import AddChildDialog from '../components/projects/hierarchy/AddChildDialog';
import RecordPickerDialog from '../components/projects/hierarchy/RecordPickerDialog';
import { setMainProject } from '../components/projects/hierarchy/linking';
import { Th, Td, DEFAULT_SORT } from './projects/tableBits';
import { useProjectFilters } from './projects/useProjectFilters';
import { useProjectStats } from './projects/useProjectStats';
import { makeProjectExport } from './projects/projectExport';
import { makeRowCells } from './projects/projectRowCells';

// onDetailChange(open): the GLOBAL KSA page shows this view under its
// Opportunities; it hides them while a project's full-page detail is open.
export default function ProjectsView({ onDetailChange } = {}) {
  const { allProjects, projectsThisYear, updateProject, saveProject, trackRecentlyViewed } = useApp();
  // Set on the GLOBAL KSA tab, which reuses this view for its own records.
  const scope = useDataScope();
  const access = useMyAccess();
  const editable = canEdit(access, 'projects');
  const initial = useMemo(() => parseHashFilters(), []);

  // Chips are a thin UI over columnFilters (see useSyncedStatusChips).
  // Default chip: Active. Legacy deep-links (?status=won etc.) fall back to All.
  const [defaultFilters] = useState(() => ({ status: new Set(['Active']) })); // the built-in Default view's
  const { columnFilters, setColumnFilters, setChip, chipValue } = useSyncedStatusChips(() => {
    const init = {};
    const s = (initial.status || '').toLowerCase();
    if (s === 'active') init.status = new Set(['Active']);
    else if (s === 'closed') init.status = new Set(['Closed']);
    else if (!initial.status) init.status = new Set(['Active']);
    // 'all' or any other legacy value -> no status filter (All)
    if (initial.customer) init.customer = new Set([initial.customer]);
    if (initial.approvalManager) init.approvalManager = new Set([initial.approvalManager]);
    return init;
  });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [openFilter, setOpenFilter] = useState(null); // column-id whose popover is open
  // The person's columns, highlights and saved views. A deep link's filters
  // (?customer=, ?status=) win over their default view's.
  const view = useTableView({
    tableKey: scope ? 'ksa-projects' : 'projects',
    defs: PROJECT_COLUMNS,
    columnFilters,
    setColumnFilters,
    sort,
    setSort,
    search,
    setSearch,
    defaultFilters,
    defaultSort: DEFAULT_SORT,
    keepFilters: !!(initial.status || initial.customer || initial.approvalManager),
    grouping: !scope, // GLOBAL KSA lists its few records flat
  });
  // Auto-open detail when arriving via deep-link (e.g. dashboard row click sets
  // hash #/projects?project=bt-0001).
  const [selectedId, setSelectedId] = useState(initial.project || null);
  // the record page's first tab: a main record opened from its group row shows its sub-projects
  const [openTab, setOpenTab] = useState(null);
  // "Add PO line" / "Add sub-project" from the right-click menu: { source, kind }
  const [adding, setAdding] = useState(null);
  // "Group under..." from the right-click menu: the record to put under a main project
  const [grouping, setGrouping] = useState(null);

  // Keep selectedId in sync with subsequent hash changes (e.g. user navigates
  // away then comes back via another deep-link).
  useEffect(() => {
    const onHash = () => {
      const params = parseHashFilters();
      if (params.project) {
        setOpenTab(params.tab || null);
        setSelectedId(params.project);
      }
    };
    window.addEventListener('hashchange', onHash);
    window.addEventListener('popstate', onHash);
    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('popstate', onHash);
    };
  }, []);
  // "+ New Project": the record editor, as a project (status Won)
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState(null);
  // ONE indexed fetch of every project task - the Tasks column reads this
  // rather than firing a request per row.
  const [taskVersion, setTaskVersion] = useState(0);
  const taskIndex = useProjectTasksIndex(taskVersion);
  const [quickTarget, setQuickTarget] = useState(null); // right-click quick add

  const selected = selectedId ? allProjects.find((p) => p.id === selectedId) : null;

  useEffect(() => {
    if (selected) trackRecentlyViewed('project', selected.id, selected.name);
  }, [selected, trackRecentlyViewed]);
  const detailOpen = !!selected;
  useEffect(() => {
    if (onDetailChange) onDetailChange(detailOpen);
  }, [detailOpen, onDetailChange]);

  // Projects = real (won) work, tracked Active/Closed. The full bid pipeline
  // (Open/Lost/Cancelled/...) lives on the Opportunities tab — this scoping is
  // what keeps the two tabs from showing identical data. The definition is
  // shared with the sidebar badge so the two can't disagree (src/data/projectStatus.js).
  const sourceProjects = useMemo(() => (projectsThisYear || []).filter(isProjectRow), [projectsThisYear]);

  const { filtered, COL_ACCESSORS, DATE_COLS, NUMERIC_COLS, MULTI_COLS, numericValueOf, money, PERCENT_COLS } = useProjectFilters({
    scope,
    sourceProjects,
    columnFilters,
    search,
    sort,
  });

  // Main project > sub-project > PO line over the records on screen (the
  // groups are the tab's records; a filter only thins what shows under them)
  const tree = useProjectTree({
    tableKey: scope ? 'ksa-projects' : 'projects',
    view,
    rows: filtered,
    source: sourceProjects,
    lookup: allProjects,
    search,
  });
  // the column that carries the tree: Description (the work's name), else the first one shown
  const treeCol = view.visible.some((c) => c.col === 'description') ? 'description' : view.visible[0] && view.visible[0].col;
  // a group row opens its record (the main project's, or the sub-project's) on its Sub-projects tab
  const openGroup = (node) => {
    setOpenTab('family');
    setSelectedId(node.mainRecordId);
  };
  const byIdAll = useMemo(() => new Map((allProjects || []).map((r) => [String(r.id), r])), [allProjects]);
  // "Remove from group": the record's Main project cleared, through the usual save (Activity, Revert, conflict guard)
  const ungroup = async (row) => {
    const res = await setMainProject(saveProject, row, null);
    setToast(
      res && res.ok
        ? `${recordLabel(row)} removed from its group`
        : `Could not remove it: ${(res && res.body && res.body.error) || 'try again'}`
    );
  };

  const { wonTotals, totalsCode, stats, statusCounts, topCustomers, topApprovalManagers, totalsMoney, columnUniqueValues } =
    useProjectStats({ COL_ACCESSORS, DATE_COLS, NUMERIC_COLS, sourceProjects, MULTI_COLS, scope, filtered });

  // The totals bar is pinned at the very TOP of the scroll pane (above the
  // column headers, right under the card's black title strip), so the sticky
  // column headers sit exactly its measured height down (usePinnedHeight).
  const [totalsRef, totalsH] = usePinnedHeight(`${filtered.length}:${view.visible.length}`);
  // the totals row's cells: the columns on screen, then Actions
  // (round 4: the empty Actions column at the far right is gone - a row opens on click)
  const totalsCols = view.visible.map((c) => c.col);

  // Clickable PO/invoice/quote numbers. Shared with the Opportunities table so
  // the two can never link a number to different files (src/hooks/useDocIndex).
  const docIdFor = useDocIndex();
  // One fetch for the Comments column, same as the Tasks column's index.
  const commentIndex = useCommentsIndex();

  const toggleSort = (col) => setSort((cur) => (cur.col === col ? { col, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' }));
  const sortIndicator = (col) => (sort.col === col ? <span className="ml-1 text-gat-green">{sort.dir === 'asc' ? '↑' : '↓'}</span> : null);

  const { handleExportCsv, handleExportXlsx, rawValue } = makeProjectExport({
    numericValueOf,
    COL_ACCESSORS,
    wonTotals,
    scope,
    tree,
    filtered,
    view,
    totalsCode,
  });
  // drag a header onto another to move it there
  // drag a header to move its column (tableViews/usePointerSort: a lifted card, the others slide)
  const headSort = usePointerSort({ axis: 'x', indicator: true, onDrop: (col, to) => view.reorder(col, to, 'visible') });
  // the view's look (tableViews/tableLook.js) and its frozen columns
  const look = lookOf(view);
  // Resized columns (round 3): fixed layout at the person's widths, Actions 28px
  const tl = tableLayout(view.visible, view.widths, 0);
  const pins = usePinnedLefts(view, (col) => tl.widthOf(view.visible.find((c) => c.col === col) || {}));
  const headRef = (el) => {
    headSort.ref.current = el;
    pins.rowRef.current = el;
  };
  const showTop = wonTotals.count > 0 && look.totalsTop;
  const showBottom = wonTotals.count > 0 && look.totalsBottom;
  // the whole row under the mouse, in the view's colour (index.css .tv-hover)
  const hov = hoverProps(view.hover);
  const { rowCells, cellExtra } = makeRowCells({ view, tl, look, COL_ACCESSORS, taskIndex, commentIndex, docIdFor, money });

  // A project opens as a page in the record editor, editable in place (its
  // Summary's fields at once, then Save); delete is on its footer. Keyed by
  // record, so another project opened from a deep link gets a fresh editor.
  if (selected) {
    return (
      <div>
        {toast && <Toast message={toast} onDone={() => setToast(null)} />}
        <EditOpportunityModal
          key={`${selected.id}:${openTab || ''}`}
          opp={selected}
          asPage
          initialTab={openTab || undefined}
          backLabel="Back to Projects"
          onClose={() => {
            setSelectedId(null);
            setOpenTab(null);
          }}
          onDeleted={() => setToast('Project deleted')}
          onOpenRecord={(id, tab) => {
            setOpenTab(tab || null);
            setSelectedId(id);
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
      {creating && (
        <EditOpportunityModal
          opp={{ status: 'Won' }}
          kind="project"
          onClose={() => setCreating(false)}
          onSaved={() => setToast('Project saved')}
        />
      )}

      {/* right-click a row → add a task or comment without opening it */}
      <QuickAddMenu
        target={quickTarget}
        onSetHealth={editable ? (row, key) => updateProject(row.id, { health: key }) : undefined}
        onClose={() => setQuickTarget(null)}
        onAdded={(msg) => {
          setTaskVersion((v) => v + 1);
          setToast(msg);
        }}
        actions={
          editable && !scope
            ? (quickTarget && parentIdOf(quickTarget.row) ? [['ungroup', '⤴  Remove from group', ungroup]] : []).concat([
                ['group-under', '⤵  Group under…', (row) => setGrouping(row)],
                ['po-line', '＋  Add PO line', (row) => setAdding({ source: row, kind: 'line' })],
                ['sub-project', '＋  Add sub-project', (row) => setAdding({ source: row, kind: 'sub' })],
              ])
            : undefined
        }
      />
      {grouping && (
        <RecordPickerDialog
          title="Group under a main project"
          intro={
            <>
              Pick the main project <b>{recordLabel(grouping)}</b> sits under.
              {parentIdOf(grouping) && (
                <>
                  {' '}
                  It is now under <b>{recordLabel(byIdAll.get(parentIdOf(grouping))) || parentIdOf(grouping)}</b>; picking another moves it.
                </>
              )}
            </>
          }
          candidates={(allProjects || []).filter(
            (r) =>
              String(r.id) !== String(grouping.id) &&
              !descendantIds(allProjects, grouping.id).has(String(r.id)) &&
              String(r.id) !== parentIdOf(grouping)
          )}
          all={allProjects}
          customer={grouping.customer}
          confirmLabel="Group under it"
          onPick={async ([main]) => {
            const res = await setMainProject(saveProject, grouping, main.id);
            if (res && res.ok) {
              setToast(`${recordLabel(grouping)} is now under ${recordLabel(main)}`);
              return null;
            }
            return { failed: [{ error: (res && res.body && res.body.error) || 'Could not save - try again.' }] };
          }}
          onClose={() => setGrouping(null)}
        />
      )}
      {adding && (
        <AddChildDialog
          source={adding.source}
          kind={adding.kind}
          onClose={() => setAdding(null)}
          onCreated={(row) =>
            setToast(`${adding.kind === 'line' ? 'PO line' : 'Sub-project'} added${row.po_number ? `: ${row.po_number}` : ''}`)
          }
        />
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-eyebrow uppercase text-text-muted mb-1">{scope ? `${scope.label} · Projects` : 'Workspace · Projects'}</div>
          <p className="text-body-sm text-text-body m-0">
            <b className="text-text-dark tabular-nums">{sourceProjects.length}</b> records ·{' '}
            <b className="text-text-dark tabular-nums">{stats.openPipeline ? fmt(stats.openPipeline) : '-'}</b> open pipeline
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleExportCsv} iconLeft={<Icon name="download" size={12} color="currentColor" />}>
            Export CSV
          </Button>
          <Button variant="secondary" onClick={handleExportXlsx} iconLeft={<Icon name="download" size={12} color="currentColor" />}>
            Export Excel
          </Button>
          {editable && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              ＋ New Project
            </Button>
          )}
        </div>
      </div>

      <div className="bg-surface border border-border rounded-md shadow-sm px-3.5 py-2.5 flex items-center gap-2 flex-wrap">
        {/* Chips write into columnFilters — the status popover mirrors them. */}
        <Chip on={chipValue('status') == null} onClick={() => setChip('status', null)}>
          All ({statusCounts.All})
        </Chip>
        <Chip
          on={chipValue('status') === 'Active'}
          tone="win"
          onClick={() => setChip('status', chipValue('status') === 'Active' ? null : 'Active')}
        >
          Active ({statusCounts.Active})
        </Chip>
        <Chip
          on={chipValue('status') === 'Closed'}
          tone="cancel"
          onClick={() => setChip('status', chipValue('status') === 'Closed' ? null : 'Closed')}
        >
          Closed ({statusCounts.Closed})
        </Chip>
        <span className="w-px h-5 bg-border-strong mx-1" />
        {topCustomers.map((c) => (
          <Chip key={c} on={chipValue('customer') === c} onClick={() => setChip('customer', chipValue('customer') === c ? null : c)}>
            {c}
          </Chip>
        ))}
        {chipValue('customer') && !topCustomers.includes(chipValue('customer')) && (
          <Chip on removable onClick={() => setChip('customer', null)} onRemove={() => setChip('customer', null)}>
            {chipValue('customer')}
          </Chip>
        )}
        {topApprovalManagers.length > 0 && <span className="w-px h-5 bg-border-strong mx-1" />}
        {topApprovalManagers.length > 0 && (
          <span className="text-[10px] uppercase tracking-[1.4px] font-bold text-text-muted self-center">AM:</span>
        )}
        {topApprovalManagers.map((m) => (
          <Chip
            key={`am-${m}`}
            on={chipValue('approvalManager') === m}
            onClick={() => setChip('approvalManager', chipValue('approvalManager') === m ? null : m)}
          >
            {m}
          </Chip>
        ))}
        {chipValue('approvalManager') && !topApprovalManagers.includes(chipValue('approvalManager')) && (
          <Chip on removable onClick={() => setChip('approvalManager', null)} onRemove={() => setChip('approvalManager', null)}>
            {chipValue('approvalManager')}
          </Chip>
        )}
        <span className="w-px h-5 bg-border-strong mx-1" />
        <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search any column…" className="w-56" />
        <TreeControls tree={tree} />
        <TableViewsBar view={view} defs={PROJECT_COLUMNS} hasGroups={tree.available} />
      </div>

      <div className="bg-surface border border-border rounded-lg shadow-md overflow-hidden">
        <div className="bg-gat-greenDarkest text-white px-4 py-3 flex items-center justify-between">
          <h3 className="text-h3 m-0">
            {chipValue('status') === 'Active' ? 'Active Projects' : chipValue('status') === 'Closed' ? 'Closed Projects' : 'All Projects'}
          </h3>
          <div className="text-meta text-white/55 tabular-nums">
            {filtered.length} visible · sorted by {sort.col} {sort.dir === 'asc' ? '↑' : '↓'}
          </div>
        </div>
        {/*
          The table's VERTICAL scrolling lives here, not on the page. This
          wrapper is already a scroll box (overflow-x:auto forces overflow-y to
          compute to `auto`), and `position: sticky` resolves against the nearest
          scroll box — so while its height was unconstrained the sticky header
          could never engage: this box never scrolled, the page scrolled behind
          it. Capping the height makes the pane the real vertical scroller, which
          is what pins the header row and the top "Won totals" row. Sticking to
          the page scroller instead is not an option — the surrounding card is
          `overflow-hidden`, which is a scroll box too.
          280px ≈ the chrome above and below the pane (64 topbar + 24/24 page
          padding + page heading + chip bar + the card's green title strip), so
          the card still lands inside the viewport and the header stays on screen.
        */}
        <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          {/*
            Hybrid layout: minimum table width is 1280px so cells never get
            crushed below readable size. On wide monitors (>= 1280px content
            area) the table fills naturally. On narrower laptop screens the
            container's overflow-x-auto kicks in and the user can scroll
            horizontally — sidebar can also be collapsed for ~184px more width.
            Each column has an explicit min-width so dollar values, dates,
            and long descriptions never overlap.
          */}
          <table
            style={{ ...tl.style, ...hov.style, ...look.tableStyle }}
            className={`${hov.className} ${look.tableClass} ${tl.fixed ? 'tv-fixed' : 'w-full min-w-[1280px]'} border-collapse text-[10.5px] [&_td]:py-1 [&_td]:px-1.5 [&_th]:px-1.5 [&_th]:py-1.5 [&_tr:hover_td]:!transition-none [&_td]:transition-none`}
          >
            {/* one <col> per column on screen, in the person's order (projectColumns.js has the widths) */}
            <colgroup>
              {view.visible.map((c) => {
                const w = tl.widthOf(c); // fixed once a column was resized (viewConfig.tableLayout)
                return (
                  <col key={c.col} style={w ? { width: w, minWidth: w, maxWidth: w } : { minWidth: c.minWidth, maxWidth: c.maxWidth }} />
                );
              })}
            </colgroup>
            <thead>
              {/* Pinned totals bar FIRST — above the column headers, directly
                  under the card's black title strip. The headers stick just
                  below it (stickyTop = its measured height). */}
              {showTop && (
                <WonTotalsRow
                  totals={wonTotals}
                  cols={totalsCols}
                  stickyTop={look.sticky ? 0 : undefined}
                  rowRef={totalsRef}
                  money={totalsMoney}
                />
              )}
              <tr ref={headRef} onPointerDown={headSort.onPointerDown}>
                {view.visible.map((c) => {
                  const isDate = DATE_COLS.has(c.col);
                  const isNumeric = NUMERIC_COLS.has(c.col);
                  const isPercent = PERCENT_COLS.has(c.col);
                  const fv = columnFilters[c.col];
                  let filterActive;
                  if (isDate) filterActive = !!(fv && (fv.from || fv.to));
                  else if (isNumeric) filterActive = !!(fv && (fv.min != null || fv.max != null));
                  else filterActive = !!(fv && fv.size > 0);
                  return (
                    <Th
                      key={c.col}
                      col={c.col}
                      sticky={look.sticky}
                      stickyTop={showTop && look.sticky ? totalsH : 0}
                      pinLeft={pins.isPinned(c.col) ? pins.lefts[c.col] || 0 : undefined}
                      align={view.align[c.col]}
                      onSort={c.sortable ? toggleSort : undefined}
                      num={c.num}
                      filterable={!c.noFilter}
                      filterActive={filterActive}
                      onFilterClick={() => setOpenFilter(openFilter === c.col ? null : c.col)}
                      className={[c.amber ? '!bg-amber-50 text-amber-800' : '', pins.last === c.col ? 'tv-pin-edge' : '']
                        .filter(Boolean)
                        .join(' ')}
                      width={tl.widthOf(c)}
                      fontSize={view.fonts[c.col] && view.fonts[c.col].head}
                      onResize={(px, measured) => view.setWidth(c.col, px, measured)}
                      onResizeReset={() => view.clearWidth(c.col)}
                      resizeLabel={c.label}
                      drag={{ 'data-sort-key': c.col, 'data-sort-label': c.label }}
                    >
                      {c.label}
                      {c.sortable && sortIndicator(c.col)}
                      {openFilter === c.col &&
                        renderFilterPopover({
                          colId: c.col,
                          columnLabel: c.label,
                          isDate,
                          isNumeric,
                          isPercent,
                          fv,
                          values: columnUniqueValues[c.col],
                          setColumnFilters,
                          sort,
                          setSort,
                          onClose: () => setOpenFilter(null),
                        })}
                    </Th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {(tree.items || filtered.map((p) => ({ node: { type: 'row', key: p.id, record: p }, depth: 0 }))).map((item, rowIdx) => {
                if (item.node.type === 'group') {
                  // a main project / sub-project: the sums of the records under it (never a row of the totals or the export)
                  const n = item.node;
                  const g = rollupRow(n.records, { key: n.key, level: n.level, gpn: n.gpn, label: n.label, total: n.total });
                  const cells = rowCells(g);
                  // its colours, size, badges and whether it shows the sums are the view's (Customize > Group rows)
                  const gl = groupLook(item, view.tree);
                  // highlight rules (Customize > Highlights) on the group's sums, cell rules only - the band stays the group's
                  const ghl = view.highlights.length ? rowHighlights(view.highlights, (col) => rawValue(g, col)) : null;
                  return (
                    <tr
                      key={`g:${n.key}`}
                      {...gl.rowProps}
                      className="border-b border-[#C9D6BF] cursor-pointer"
                      onClick={() => openGroup(n)}
                      title={`Open the ${n.level === 'main' ? 'main project' : 'sub-project'}`}
                    >
                      {view.visible.map(({ col }) => {
                        const c = cells[col];
                        const widget = col === 'tasks' || col === 'comments' || (c.num && !gl.totals);
                        const hs = ghl && !widget ? highlightStyle(ghl.cells[col] || null) : null;
                        return (
                          <Td
                            key={col}
                            hl={!!(hs && hs.background)}
                            num={c.num}
                            className={`${c.className || ''}${pins.last === col ? ' tv-pin-edge' : ''}`}
                            style={{ ...c.style, ...alignOf(view, col), ...pinStyle(pins, col), ...gl.cellStyle, ...hs }}
                          >
                            {col === treeCol ? (
                              <TreeGroupLabel item={item} onToggle={tree.toggle} tree={view.tree} />
                            ) : widget ? null : (
                              c.children
                            )}
                          </Td>
                        );
                      })}
                    </tr>
                  );
                }
                const p = item.node.record;
                const cells = rowCells(p);
                const hl = view.highlights.length ? rowHighlights(view.highlights, (col) => rawValue(p, col)) : null;
                const hlOf = (col) => highlightStyle(cellLook(hl, col));
                const hit = matchStyle(item);
                return (
                  <tr
                    key={p.id}
                    onClick={() => setSelectedId(p.id)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setQuickTarget({ row: p, x: e.clientX, y: e.clientY });
                    }}
                    className={[
                      // Hover colour is NOT set here: the [data-health] rule in
                      // index.css paints every cell, in a deeper shade of the
                      // row's own flag, so a red row stays red under the mouse.
                      'group border-b border-[#D8DED4] cursor-pointer',
                    ].join(' ')}
                    data-health={healthOf(p)}
                    data-highlight={hl && hl.row ? 'row' : undefined}
                    data-depth={item.depth || undefined}
                    style={{
                      // The tint reaches every cell via the [data-health] rule
                      // in index.css - the PO and invoice groups paint their
                      // own backgrounds and used to cover a colour set here.
                      // A highlight rule sets the same variable on its cells.
                      // zebra rows off (Customize > Appearance): every row in the first stripe
                      '--health-tint': rowTint(p, look.zebra ? rowIdx : 0),
                      '--health-hover': rowHover(p),
                      // flagged rows also get a coloured edge, so a red job is
                      // findable while scanning rather than only in its Status cell
                      ...(healthOf(p) === 'green' ? {} : { boxShadow: `inset 4px 0 0 ${HEALTH[healthOf(p)].dot}` }),
                    }}
                  >
                    {view.visible.map(({ col }) => {
                      const c = cells[col];
                      const hs = hlOf(col);
                      const x = cellExtra(p, col, c.title);
                      const al = alignOf(view, col);
                      const pin = pinStyle(pins, col);
                      // the tree column: indented under its group; a match among filtered-out siblings gets an amber edge
                      const inTree = col === treeCol && item.depth > 0;
                      const mark = col === treeCol ? hit : null;
                      return (
                        <Td
                          key={col}
                          hl={!!(hs && hs.background)}
                          num={c.num}
                          className={`${c.className || ''}${pins.last === col ? ' tv-pin-edge' : ''}`}
                          title={x.title}
                          style={hs || x.style || al || pin || mark ? { ...c.style, ...x.style, ...al, ...pin, ...mark, ...hs } : c.style}
                        >
                          {inTree ? (
                            <TreeIndent item={item} lines={view.tree.lines}>
                              {c.children}
                            </TreeIndent>
                          ) : (
                            c.children
                          )}
                        </Td>
                      );
                    })}
                  </tr>
                );
              })}
              {showBottom && <WonTotalsRow totals={wonTotals} cols={totalsCols} money={totalsMoney} />}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-10 text-center text-text-body">
            No projects match these filters.{' '}
            <button
              type="button"
              className="text-gat-green font-semibold ml-1 hover:underline"
              onClick={() => {
                setColumnFilters({});
                setSearch('');
              }}
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
